/**
 * Simple Report Exporter
 * Takes the raw Gemini output and formats it directly as HTML
 * No complex parsing or extraction - just clean formatting
 */

/**
 * Export SEO audit to Google Docs compatible HTML
 * @param auditResult SEO audit result (any type that has geminiAudit and url properties)
 * @returns HTML string
 */
export function simpleExportToGoogleDocs(auditResult: any): string {
  // Get the raw Gemini output
  const rawOutput = auditResult.geminiAudit?.rawOutput || '';
  
  // Get basic audit info
  const url = auditResult.url || '';
  const date = new Date().toLocaleDateString();
  
  // Convert the raw output to HTML with proper formatting
  let formattedContent = '';
  
  if (rawOutput) {
    // Split by lines and process
    const lines = rawOutput.split('\n');
    let inList = false;
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        formattedContent += '<br>';
        return;
      }
      
      // Handle headings
      if (trimmedLine.startsWith('# ')) {
        formattedContent += `<h1>${trimmedLine.substring(2)}</h1>`;
      } else if (trimmedLine.startsWith('## ')) {
        formattedContent += `<h2>${trimmedLine.substring(3)}</h2>`;
      } else if (trimmedLine.startsWith('### ')) {
        formattedContent += `<h3>${trimmedLine.substring(4)}</h3>`;
      } else if (trimmedLine.startsWith('SEO Analysis for ')) {
        formattedContent += `<h1>${trimmedLine}</h1>`;
      } else if (trimmedLine.match(/^[A-Z][a-z]+ (Analysis|Assessment|SEO|Issues)/) || 
                trimmedLine.match(/^(Technical SEO|Content Analysis|On-Page SEO|Performance|Critical Issues)/)) {
        formattedContent += `<h2>${trimmedLine}</h2>`;
      } else if (trimmedLine.startsWith('Score:')) {
        const scoreMatch = trimmedLine.match(/Score:\s*(\d+)\/100/);
        const score = scoreMatch ? parseInt(scoreMatch[1]) : 0;
        const scoreClass = score >= 80 ? 'high' : (score >= 60 ? 'medium' : 'low');
        
        formattedContent += `<p class="score score-${scoreClass}" style="font-weight: bold; font-size: 18px;">${trimmedLine}</p>`;
      } else if (trimmedLine.startsWith('- ')) {
        // Start list if not already in one
        if (!inList) {
          formattedContent += '<ul>';
          inList = true;
        }
        formattedContent += `<li>${trimmedLine.substring(2)}</li>`;
      } else {
        // End list if we were in one
        if (inList) {
          formattedContent += '</ul>';
          inList = false;
        }
        formattedContent += `<p>${trimmedLine}</p>`;
      }
    });
    
    // Close any open list
    if (inList) {
      formattedContent += '</ul>';
    }
  } else {
    formattedContent = '<p>No SEO analysis data available.</p>';
  }
  
  // Create the full HTML document
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>SEO Audit Report - ${url}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }
        h1 {
          color: #2c3e50;
          border-bottom: 2px solid #3498db;
          padding-bottom: 10px;
          margin-top: 30px;
        }
        h2 {
          color: #2980b9;
          margin-top: 25px;
        }
        h3 {
          color: #3498db;
          margin-top: 20px;
        }
        p {
          margin: 10px 0;
        }
        ul {
          margin: 10px 0;
          padding-left: 20px;
        }
        li {
          margin: 5px 0;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
        }
        .score {
          display: inline-block;
          padding: 5px 10px;
          border-radius: 4px;
          font-weight: bold;
        }
        .score-high {
          color: #27ae60;
        }
        .score-medium {
          color: #f39c12;
        }
        .score-low {
          color: #e74c3c;
        }
        .page-break {
          page-break-after: always;
        }
        .footer {
          margin-top: 50px;
          text-align: center;
          font-size: 12px;
          color: #7f8c8d;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>SEO Audit Report</h1>
        <p><strong>Website:</strong> ${url}</p>
        <p><strong>Date:</strong> ${date}</p>
      </div>
      
      ${formattedContent}
      
      <div class="footer">
        <p>Report generated by MarketNest SEO Audit Tool</p>
      </div>
    </body>
    </html>
  `;
  
  return html;
}
